/**
 * Frontend Client Isolation Service for SCULPTD APP
 * Ensures proper client identification and data isolation in the frontend
 */

export interface ClientContext {
  clientId: string;
  tenantId: string;
  userId: string;
  sessionId: string;
  dataPartition: string;
  encryptionLevel: 'standard' | 'enhanced' | 'military';
  privacyLevel: 'public' | 'internal' | 'confidential' | 'restricted';
  timestamp: string;
}

export interface ClientIsolationConfig {
  clientId: string;
  tenantId: string;
  userId: string;
  sessionId: string;
  autoGenerate: boolean;
  persistInStorage: boolean;
  encryptionEnabled: boolean;
}

export class ClientIsolationService {
  private static instance: ClientIsolationService;
  private clientContext: ClientContext | null = null;
  private config: ClientIsolationConfig | null = null;

  private constructor() {
    this.initializeFromStorage();
  }

  public static getInstance(): ClientIsolationService {
    if (!ClientIsolationService.instance) {
      ClientIsolationService.instance = new ClientIsolationService();
    }
    return ClientIsolationService.instance;
  }

  /**
   * Initialize client isolation with configuration
   */
  public initialize(config: ClientIsolationConfig): ClientContext {
    this.config = config;
    
    // Generate client identifiers if needed
    const clientId = config.clientId || (config.autoGenerate ? this.generateClientId() : '');
    const tenantId = config.tenantId || 'default';
    
    if (!clientId) {
      throw new Error('Client ID is required for isolation');
    }

    this.clientContext = {
      clientId,
      tenantId,
      userId: config.userId,
      sessionId: config.sessionId,
      dataPartition: `tenant_${tenantId}_client_${clientId}`,
      encryptionLevel: 'enhanced',
      privacyLevel: 'confidential',
      timestamp: new Date().toISOString()
    };

    if (config.persistInStorage) {
      this.persistToStorage();
    }

    return this.clientContext;
  }

  /**
   * Get current client context
   */
  public getClientContext(): ClientContext | null {
    return this.clientContext;
  }

  /**
   * Get client headers for API requests
   */
  public getClientHeaders(): Record<string, string> {
    if (!this.clientContext) {
      throw new Error('Client context not initialized');
    }

    return {
      'X-Client-ID': this.clientContext.clientId,
      'X-Tenant-ID': this.clientContext.tenantId,
      'X-User-ID': this.clientContext.userId,
      'X-Session-ID': this.clientContext.sessionId
    };
  }

  /**
   * Validate client access to operation
   */
  public validateAccess(operation: string): boolean {
    if (!this.clientContext) {
      return false;
    }

    // SCULPTD-specific operations
    const allowedOperations = [
      'sculptd_fitness_coaching',
      'sculptd_nutrition_analysis',
      'sculptd_progress_tracking',
      'sculptd_medical_assessment',
      'sculptd_goal_setting',
      'sculptd_knowledge_search'
    ];

    return allowedOperations.includes(operation);
  }

  /**
   * Encrypt sensitive data with client-specific key
   */
  public encryptData(data: any): string {
    if (!this.clientContext) {
      throw new Error('Client context not initialized');
    }

    // In production, use proper encryption with client-specific keys
    const encryptedData = {
      ...data,
      clientId: this.clientContext.clientId,
      tenantId: this.clientContext.tenantId,
      timestamp: new Date().toISOString(),
      encrypted: true
    };

    return btoa(JSON.stringify(encryptedData));
  }

  /**
   * Decrypt client data
   */
  public decryptData(encryptedData: string): any {
    if (!this.clientContext) {
      throw new Error('Client context not initialized');
    }

    try {
      const decryptedData = JSON.parse(atob(encryptedData));
      
      // Verify client ownership
      if (decryptedData.clientId !== this.clientContext.clientId || 
          decryptedData.tenantId !== this.clientContext.tenantId) {
        throw new Error('Data access violation: Client mismatch');
      }

      return decryptedData;
    } catch (error) {
      throw new Error('Failed to decrypt data: ' + error);
    }
  }

  /**
   * Create isolated API request
   */
  public createIsolatedRequest(url: string, options: RequestInit = {}): RequestInit {
    if (!this.clientContext) {
      throw new Error('Client context not initialized');
    }

    const headers = {
      ...this.getClientHeaders(),
      ...options.headers
    };

    return {
      ...options,
      headers
    };
  }

  /**
   * Get client isolation status
   */
  public getIsolationStatus(): any {
    if (!this.clientContext) {
      return {
        initialized: false,
        error: 'Client context not initialized'
      };
    }

    return {
      initialized: true,
      clientId: this.clientContext.clientId,
      tenantId: this.clientContext.tenantId,
      userId: this.clientContext.userId,
      sessionId: this.clientContext.sessionId,
      dataPartition: this.clientContext.dataPartition,
      encryptionLevel: this.clientContext.encryptionLevel,
      privacyLevel: this.clientContext.privacyLevel,
      timestamp: this.clientContext.timestamp
    };
  }

  /**
   * Clean up client data (for logout/GDPR compliance)
   */
  public cleanup(): void {
    this.clientContext = null;
    this.config = null;
    this.clearStorage();
  }

  /**
   * Generate unique client ID
   */
  private generateClientId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `client_${timestamp}_${random}`;
  }

  /**
   * Initialize from localStorage
   */
  private initializeFromStorage(): void {
    try {
      const stored = localStorage.getItem('sculptd_client_context');
      if (stored) {
        this.clientContext = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load client context from storage:', error);
    }
  }

  /**
   * Persist to localStorage
   */
  private persistToStorage(): void {
    if (this.clientContext) {
      try {
        localStorage.setItem('sculptd_client_context', JSON.stringify(this.clientContext));
      } catch (error) {
        console.warn('Failed to persist client context to storage:', error);
      }
    }
  }

  /**
   * Clear localStorage
   */
  private clearStorage(): void {
    try {
      localStorage.removeItem('sculptd_client_context');
    } catch (error) {
      console.warn('Failed to clear client context from storage:', error);
    }
  }
}

export const clientIsolationService = ClientIsolationService.getInstance();